import { Injectable } from '@angular/core';
import { APIService, KPI } from '../api';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, shareReplay, switchMap, tap } from 'rxjs/operators';
import { INFLATION_HYPOTHESIS_STORAGE_KEY } from './constants';

@Injectable({
  providedIn: 'root',
})
export class InflationService {
  private _selectedInflationHypothesis$ = new BehaviorSubject<KPI.InflationHypothesis>(null);
  private cachedSelectedInflationHypothesIsLoaded: boolean = false;


  private _selectedInflationHypothesisFiltered$ = new BehaviorSubject<KPI.InflationHypothesis>(null);
  private cachedSelectedInflationHypothesFilteredIsLoaded: boolean = false;

  constructor(
    private apiService: APIService,
  ) { }

  private inflationHypotheses: Observable<KPI.InflationHypothesis[]>;
  public get inflationHypotheses$(): Observable<KPI.InflationHypothesis[]> {
    if (!this.inflationHypotheses) {
      this.inflationHypotheses = this.apiService.get_energy_price_hypotheses().pipe(
        shareReplay(1),
      );
    }
    return this.inflationHypotheses;
  }

  public get selectedInflationHypothesis$(): Observable<KPI.InflationHypothesis> {
    return this.inflationHypotheses$.pipe(
      first(),
      tap((inflationHypotheses) => {
        if (!this.cachedSelectedInflationHypothesIsLoaded) {
          const cachedHypothesis = localStorage.getItem(
            INFLATION_HYPOTHESIS_STORAGE_KEY,
          );
          if (inflationHypotheses.length) {
            if (cachedHypothesis) {
              const parsedCachedHypothesis = JSON.parse(cachedHypothesis);
              if (parsedCachedHypothesis) {
                const cachedHypothesisObject = inflationHypotheses.find(
                  (h) => h.id === parsedCachedHypothesis.id,
                );
                if (cachedHypothesisObject) {
                  this._selectedInflationHypothesis$.next(cachedHypothesisObject);
                } else {
                  this._selectedInflationHypothesis$.next(inflationHypotheses[0]);
                }
              } else {
                this._selectedInflationHypothesis$.next(inflationHypotheses[0]);
              }
            } else {
              this._selectedInflationHypothesis$.next(inflationHypotheses[0]);
            }
          } else {
            this._selectedInflationHypothesis$.next(null);
          }
          this.cachedSelectedInflationHypothesIsLoaded = true;
        }
      }),
      switchMap(() => this._selectedInflationHypothesis$),
    );
  }

  public selectedInflationHypothesisFiltered$(forcedEnergyType: number): Observable<KPI.InflationHypothesis> {
    return this.inflationHypotheses$.pipe(
      first(),
      tap((inflationHypotheses) => {
        if (forcedEnergyType !== undefined && forcedEnergyType !== null) {
          // If an energy if enforced (e.g.: initiative's energy), make sure we don't select a Hypothesis
          // which doesn't have any daat for this energy, unless the backend could return an error
          inflationHypotheses = inflationHypotheses.filter((hypothesis) =>
            hypothesis.energy_types
            && hypothesis.energy_types.length
            && hypothesis.energy_types.find((energyType) =>
              energyType.id === forcedEnergyType
              && energyType.prices
              && energyType.prices.length
            )
          );
        }
        if (!this.cachedSelectedInflationHypothesFilteredIsLoaded || forcedEnergyType !== null) {
          const cachedHypothesis = localStorage.getItem(
            INFLATION_HYPOTHESIS_STORAGE_KEY,
          );
          if (inflationHypotheses.length) {
            if (cachedHypothesis) {
              const parsedCachedHypothesis = JSON.parse(cachedHypothesis);
              if (parsedCachedHypothesis) {
                const cachedHypothesisObject = inflationHypotheses.find(
                  (h) => h.id === parsedCachedHypothesis.id,
                );
                if (cachedHypothesisObject) {
                  this._selectedInflationHypothesisFiltered$.next(cachedHypothesisObject);
                } else {
                  this._selectedInflationHypothesisFiltered$.next(inflationHypotheses[0]);
                }
              } else {
                this._selectedInflationHypothesisFiltered$.next(inflationHypotheses[0]);
              }
            } else {
              this._selectedInflationHypothesisFiltered$.next(inflationHypotheses[0]);
            }
          } else {
            this._selectedInflationHypothesisFiltered$.next(null);
          }
          this.cachedSelectedInflationHypothesFilteredIsLoaded = forcedEnergyType !== null;
        }
      }),
      switchMap(() => this._selectedInflationHypothesisFiltered$),
    );
  }

  public setSelectedInflationHypothesis(hypothesisId: number): void {
    if (!this._selectedInflationHypothesis$.value || hypothesisId !== this._selectedInflationHypothesis$.value.id) {
      this.inflationHypotheses$
        .pipe(first())
        .subscribe((inflationHypotheses) => {
          const cachedHypothesisObject = inflationHypotheses.find(
            (h) => h.id === Number(hypothesisId),
          );
          if (cachedHypothesisObject) {
            this._selectedInflationHypothesis$.next(cachedHypothesisObject);
            this._selectedInflationHypothesisFiltered$.next(cachedHypothesisObject);
            localStorage.setItem(
              INFLATION_HYPOTHESIS_STORAGE_KEY,
              JSON.stringify(cachedHypothesisObject),
            );
          }
        });
    }
  }
}
